// Google's click identifiers, the counterpart of lib/metaClickIds.js.
//
// Why the server needs them: since the trial is handed over at email capture,
// the trial starts server-side and the browser never fires `trial_start`. For
// GA4 to attach that server event to this visitor's session — and for Ads to
// attribute it to the click that paid for it — the request has to carry the
// GA4 client id, and the gclid when there is one.
//
// The cookie reader is duplicated from metaClickIds.js rather than extracted
// into a shared module on purpose: that file is load-bearing for the Meta
// funnel, and a one-line refactor there is not worth the risk of disturbing
// it. If a third caller ever needs cookies, extract then.

function readCookie(name) {
  try {
    const entry = document.cookie
      .split(";")
      .map((part) => part.trim())
      .find((part) => part.startsWith(`${name}=`));
    return entry ? decodeURIComponent(entry.slice(name.length + 1)) : null;
  } catch {
    return null;
  }
}

// The `_ga` cookie reads `GA1.1.<random>.<timestamp>`, and GA4's client id is
// the last two fields joined — not the whole cookie. Sending the raw cookie
// value would be treated as an unknown id, which is the silent failure this
// function exists to avoid: the event would land as a brand-new user with no
// campaign attached.
export function ga4ClientId() {
  const raw = readCookie("_ga");
  if (!raw) return null;
  const parts = raw.split(".");
  if (parts.length < 4) return null;
  return parts.slice(2).join(".");
}

// The per-stream session cookie is `_ga_<measurement id without the "G-">`
// and reads `GS1.1.<session id>.<session number>....`. Without the session id
// a Measurement Protocol event is recorded but session-less: it gets no
// session-scoped campaign, and it does not surface in Realtime or the standard
// reports the way anyone looking for it would expect. Sending it is what makes
// the server event land inside the visit that produced it.
const GA4_SESSION_COOKIE = "_ga_BNSPX11VYK";

export function ga4SessionId() {
  const raw = readCookie(GA4_SESSION_COOKIE);
  if (!raw) return null;
  const parts = raw.split(".");
  if (parts.length < 3) return null;
  return parts[2] || null;
}

export function googleClickIds() {
  try {
    const gclid = new URLSearchParams(window.location.search).get("gclid") || null;
    return {
      ga_client_id: ga4ClientId(),
      ga_session_id: ga4SessionId(),
      gclid,
    };
  } catch {
    return {};
  }
}
